import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useQuestionnaireStore from "../store/useQuestionnaireStore";
import {
  Sparkles,
  Activity,
  CheckCircle,
  Home,
  Lock,
  ShieldCheck,
  Video,
  X
} from "lucide-react";
import Button from "../components/common/Button";

// API Key and model managed by environment variables
const apiKey = process.env.REACT_APP_GEMINI_API_KEY || "AIzaSyAFxKk3wem13R4KkamCwakcUNOq-tN-6yI";
const geminiModel = process.env.REACT_APP_GEMINI_MODEL || "gemini-3.5-flash";

const PatientReport = () => {
  const navigate = useNavigate();
  const store = useQuestionnaireStore();

  const [loading, setLoading] = useState(true);
  const [aiResponse, setAiResponse] = useState(null);
  const [error, setError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Auto-trigger AI generation on mount
  useEffect(() => {
    if (!store.painLocation) {
      navigate('/', { replace: true });
      return;
    }

    const compileReport = () => {
      const report = {
        "Primary Location": store.painLocation,
        "Specific Area": store.preciseLocation?.name || "General",
        "Age": store.age,
        "Gender": store.gender,
        "Duration": store.painDuration,
        "Onset": `${store.onset} ${store.onsetDetail ? `(${store.onsetDetail})` : ''}`,
        "Intensity": `${store.painIntensity}/10`,
        "Sensation": store.painType,
        "Aggravating Factors": Array.isArray(store.aggravatingFactor) ? store.aggravatingFactor.join(", ") : store.aggravatingFactor,
        "Relieving Factors": Array.isArray(store.relievingFactor) ? store.relievingFactor.join(", ") : store.relievingFactor,
        "Swelling": store.hasSwelling ? "Yes" : "No",
        "Pressure Sensitivity": store.worsensWithPressure ? "Yes" : "No",
        "Medical History": store.medicalConditions.join(", ")
      };
      return JSON.stringify(report, null, 2);
    };

    const generateAI = async () => {
      const patientData = compileReport();
      const prompt = `
        Act as an expert Physiotherapist. Analyze this patient data:
        ${patientData}

        Identify the "Primary Pattern" of the condition.

        Provide a recovery plan in PLAIN ENGLISH. 
        Format the response as valid JSON with this structure ONLY:
        {
          "primary_pattern": {
              "name": "Name of the pattern",
              "short_summary": "A 1-2 sentence overview suitable for a quick glance.",
              "detailed_analysis": "In-depth explanation of why this pattern was detected based on the user's specific data."
          },
          "insights": ["Point 1", "Point 2"],
          "exercises": [
             {"name": "Exercise Name", "instruction": "Brief how-to."}
          ],
          "video_terms": ["Search Term 1", "Search Term 2"]
        }
      `;

      try {
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/${geminiModel}:generateContent?key=${apiKey}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              contents: [{ parts: [{ text: prompt }] }],
              generationConfig: { responseMimeType: "application/json" }
            }),
          }
        );

        if (!response.ok) throw new Error("AI Service Unavailable");

        const data = await response.json();
        const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
        setAiResponse(JSON.parse(text));
        setLoading(false);
      } catch (err) {
        console.error("AI Error:", err);
        setError(true);
        setLoading(false);
      }
    };

    generateAI();
  }, [navigate, store]);

  return (
    <motion.div
      className="page patient-report-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="report-header">
        <h1>Your Recovery Plan</h1>
        <p>Generated by Clinical AI based on your assessment.</p>
      </div>

      <div className="report-body">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <h3>Analyzing your bio-mechanics...</h3>
            <p>Our AI is consulting physiotherapy protocols to build your plan.</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>We couldn't generate your report right now. Please try again later.</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        ) : aiResponse ? (
          <motion.div
            className="results-container"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* 1. Primary Pattern Card (Summary Size) */}
            {aiResponse.primary_pattern && (
              <div className="pattern-card summary-card">
                <div className="card-icon pattern-icon">
                  <Activity size={24} />
                </div>
                <div className="card-content">
                  <h3>{aiResponse.primary_pattern.name}</h3>
                  <p>{aiResponse.primary_pattern.short_summary}</p>
                  <button
                    className="read-more-btn"
                    onClick={() => setIsModalOpen(true)}
                  >
                    Read Full Analysis
                  </button>
                </div>
              </div>
            )}

            {/* 3. SAFETY VERIFICATION GATE (Replaces Exercises/Videos) */}
            <div className="safety-gate-card">
              <div className="gate-header">
                <ShieldCheck size={32} className="shield-icon" />
                <h3>Safety Verification Required</h3>
              </div>

              <p className="gate-warning">
                We cannot unlock medical protocols without human verification.
                A certified Physiotherapist must review your movement to ensure this plan is safe for you.
              </p>

              <div className="booking-box">
                <div className="booking-header">
                  <span className="step-badge">Step 2: Safety Triage</span>
                  <div className="price-tag">₹499</div>
                </div>

                <div className="booking-content">
                  <h4><Video size={20} /> Quick Physio Video Call</h4>
                  <ul className="benefits-list">
                    <li><CheckCircle size={16} /> Confirm your Diagnosis</li>
                    <li><CheckCircle size={16} /> Rule out Red Flags</li>
                    <li><CheckCircle size={16} /> Unlock Personalized Exercises</li>
                  </ul>
                </div>

                <Button className="unlock-btn" onClick={() => console.log("Book Session")}>
                  <Lock size={18} /> Book Safety Call To Unlock Plan
                </Button>
              </div>
            </div>

          </motion.div>
        ) : null}
      </div>

      {!loading && (
        <div className="footer-actions">
          <button
            className="home-link"
            onClick={() => {
              store.resetStore();
              navigate('/');
            }}
          >
            <Home size={18} /> Back to Home
          </button>
        </div>
      )}

      {/* --- Detailed Report Modal --- */}
      <AnimatePresence>
        {isModalOpen && aiResponse && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.95, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="modal-close-btn"
                onClick={() => setIsModalOpen(false)}
              >
                <X size={24} />
              </button>

              <div className="modal-header">
                <h2>{aiResponse.primary_pattern.name}</h2>
              </div>

              <div className="modal-body">
                <div className="modal-section">
                  <h4><Activity size={20} className="icon" /> Detailed Analysis</h4>
                  <p>{aiResponse.primary_pattern.detailed_analysis}</p>
                </div>

                <div className="modal-section section-card">
                  <h4><Sparkles size={20} className="icon" /> Key Insights</h4>
                  <ul>
                    {aiResponse.insights?.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PatientReport;